import { useEffect, useState, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getSection, selectSections } from "../../store/slices/configSlice";
import { setSnackAction } from "../../store/slices/snackSlice";
import "../../vendor/perfect-scrollbar.css";
import PerfectScrollbar from "react-perfect-scrollbar";
import axios from "../../plugins/axios";
import MyCKEditor from "../../ckeditor/MyCKEditor";
import { pageMargins } from "../../ckeditor/pageMargins";
import {
    getNodesByType,
    setPlaceholderValue,
    setSectionContent,
} from "../../ckeditor/ckEditorUtils";
import QuestionCard from "./QuestionCard";
import axios_raw from "axios";
import ckcss from "../../ckeditor/ck-content.css?inline";
import documentService from "../../services/documentService";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogActions,
    TextField,
    Alert,
    Autocomplete,
} from "@mui/material";
import { Gavel as GavelIcon, Publish as PublishIcon } from "@mui/icons-material";
import { ReactComponent as DocImage } from "../../images/file_doc.svg?react";
import { ReactComponent as PdfImage } from "../../images/file_pdf.svg?react";

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

interface AccountOption {
    id: number | string;
    text: string;
    value: number | string;
}

interface SelectedAccount {
    accountId?: number | string;
    accountName?: string;
    externalId?: number | string;
}

interface QuestionItem {
    key: string;
    id?: number | string;
    type: "placeholder" | "section";
    value?: any;
    isMandatory?: boolean;
    dependsOnSectionId?: number | string;
    hidden?: boolean;
    [k: string]: any;
}

const TemplateDocument = () => {
    const dispatch = useAppDispatch();
    const documentTemplateId = useAppSelector((state) => state.configReducer.documentTemplateId);
    const sections = useAppSelector(selectSections);
    const setSnack = (message: string, severity: "success" | "info" | "warning" | "error") =>
        dispatch(setSnackAction(message, severity));

    const [isLoading, setIsLoading] = useState(false);
    const [editor, setEditor] = useState<any>(null);
    const [template, setTemplate] = useState<any>(null);
    const [downloading, setDownloading] = useState(false);
    const [questions, setQuestions] = useState<QuestionItem[]>([]);
    const [sectionQuestions, setSectionQuestions] = useState<QuestionItem[]>([]);
    const [stage, setStage] = useState(1);
    const [publishing, setPublishing] = useState(false);
    const [savedAnswers, setSavedAnswers] = useState<Record<string, any> | undefined>();

    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
    const [accountText, setAccountText] = useState("");
    const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);

    const [requireConfirmation, setRequireConfirmation] = useState(false);
    const [pendingUpdates, setPendingUpdates] = useState<(number | string)[]>([]);

    const handleAccountChange = (_e: any, v: AccountOption | string | null) => {
        if (v && typeof v === "object") {
            setSelectedAccount({ accountId: v.id, accountName: v.text, externalId: v.value });
        } else {
            setSelectedAccount(null);
        }
    };

    useEffect(() => {
        const getAnswers = async () => {
            if (!selectedAccount?.externalId) return;
            const answers = await documentService.getAnswers(String(selectedAccount.externalId));
            setSavedAnswers(answers as Record<string, any>);
        };

        if (selectedAccount) getAnswers();
    }, [selectedAccount]);

    useEffect(() => {
        let active = true;

        if (!accountText || accountText.length < 3) {
            setAccountOptions([]);
            return undefined;
        }

        (async () => {
            const clients = (
                await axios.get<AccountOption[]>(`${apiUrl}/getclients/` + encodeURIComponent(accountText))
            ).data;
            if (active) setAccountOptions(clients);
        })();

        return () => {
            active = false;
        };
    }, [autocompleteOpen, accountText]);

    const styles = { targetDiv: { height: "calc(100vh - 180px)" } };

    const getTemplate = useCallback(async () => {
        setIsLoading(true);
        const response = await axios.get(`${apiUrl}/gettemplate/${documentTemplateId}`);
        setTemplate(response.data);
        setIsLoading(false);
    }, [documentTemplateId]);

    useEffect(() => {
        if (documentTemplateId && !template) getTemplate();
    }, [getTemplate, documentTemplateId, template]);

    const populateQuestions = useCallback(() => {
        if (editor && savedAnswers) {
            editor.model.change((writer: any) => {
                const root = editor.model.document.getRoot();
                const placeholderElements: any[] = getNodesByType(writer, "placeholder", root);

                const usedKeys: string[] = [];
                const tempQuestions: QuestionItem[] = [];
                for (const e of placeholderElements) {
                    const obj: QuestionItem = { ...e.getAttribute("placeholder"), type: "placeholder" };
                    obj.value = e.getAttribute("value") || savedAnswers[obj.key];
                    if (!usedKeys.includes(obj.key)) {
                        usedKeys.push(obj.key);
                        tempQuestions.push(obj);
                        setPlaceholderValue(editor, obj.key, obj.value);
                    }
                }

                setQuestions(tempQuestions);
            });
        }
    }, [editor, savedAnswers]);

    useEffect(() => {
        populateQuestions();
    }, [populateQuestions]);

    useEffect(() => {
        if (!template || !editor) return;
        editor.setData(template.html);
    }, [template, editor]);

    const populateSectionContent = useCallback(
        (key: string, newQuestions: QuestionItem[]) => {
            const matchedSections = sections.filter((x: any) => x.key === key);
            const firstYes = newQuestions.find(
                (x) => x.key === key && (x.value === "Yes" || x.isMandatory),
            );
            const matchedSection =
                firstYes && matchedSections.find((x: any) => x.sectionId === firstYes.id && !firstYes.hidden);

            const retValue = matchedSection && !matchedSection.html && matchedSection.sectionId;

            if (!retValue) {
                setSectionContent(
                    editor,
                    key,
                    matchedSection
                        ? matchedSection.html || "LOADING ...."
                        : matchedSections.length > 1
                        ? "MULTI CHOICE"
                        : "",
                    populateQuestions,
                );
            }

            return retValue;
        },
        [editor, populateQuestions, sections],
    );

    const doUpdates = (keys: string[], qs: QuestionItem[]) => {
        const sectionIds: (number | string)[] = [];

        for (const k of keys) {
            const sectionId = populateSectionContent(k, qs);
            if (sectionId) sectionIds.push(sectionId);
        }

        if (sectionIds.length > 0) setPendingUpdates([...pendingUpdates, ...sectionIds]);

        for (const sectionId of sectionIds) dispatch(getSection(sectionId));
    };

    useEffect(() => {
        if (editor && template && savedAnswers) {
            const keysToUpdate = Object.keys(savedAnswers);

            editor.model.change((writer: any) => {
                const root = editor.model.document.getRoot();
                const usedKeys: string[] = [];

                const tempQuestions: QuestionItem[] = [];
                const sectionElements: any[] = getNodesByType(writer, "htmlSection", root);
                for (const e of sectionElements) {
                    // CKEditor 5 v37+ renamed `htmlAttributes` to per-element-type
                    // names; `htmlSection` elements now use `htmlSectionAttributes`.
                    const htmlAttributes = e.getAttribute("htmlSectionAttributes");
                    const key = htmlAttributes?.attributes?.["data-key"];
                    if (!key) continue;

                    if (!usedKeys.includes(key)) {
                        usedKeys.push(key);

                        const matchedSections = sections.filter((x: any) => x.key === key);

                        for (const matchedSection of matchedSections as any[]) {
                            if (matchedSection.isMandatory) keysToUpdate.push(key);

                            tempQuestions.push({
                                key,
                                id: matchedSection.sectionId,
                                type: "section",
                                dependsOnSectionId: matchedSection.dependsOnSectionId,
                                isMandatory: matchedSection.isMandatory,
                                value:
                                    matchedSections.length === 1
                                        ? savedAnswers[key]
                                        // eslint-disable-next-line eqeqeq
                                        : savedAnswers[key] == matchedSection.sectionId
                                        ? "Yes"
                                        : savedAnswers[key] && "No",
                            });
                        }
                    }
                }

                setSectionQuestions(tempQuestions);

                for (const tempQuestion of tempQuestions) {
                    if (tempQuestion.dependsOnSectionId) {
                        const parentQuestion = tempQuestions.find(
                            (x) => x.id === tempQuestion.dependsOnSectionId,
                        );
                        if (parentQuestion && parentQuestion.value !== "Yes") {
                            tempQuestion.hidden = true;
                            keysToUpdate.push(tempQuestion.key);
                        }
                    }
                }

                doUpdates(keysToUpdate, tempQuestions);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, template, setSectionQuestions, savedAnswers]);

    const displayQuestions = useMemo(() => {
        return stage === 1 ? sectionQuestions.filter((x) => !x.hidden) : questions;
    }, [stage, sectionQuestions, questions]);

    const progress = useMemo(() => {
        const countableQuestions = displayQuestions.filter(
            (q) =>
                q.type === "placeholder" || sections.some((s: any) => s.key === q.key && !s.isMandatory),
        );

        const total = countableQuestions.length;
        if (total === 0) return 100;

        const completed = countableQuestions.filter((m) => m.value);
        return (completed.length * 100) / total;
    }, [displayQuestions, sections]);

    useEffect(() => {
        if (pendingUpdates.length > 0) {
            const actioned: (number | string)[] = [];
            for (const id of pendingUpdates) {
                const s: any = sections.find((x: any) => x.sectionId === id);
                if (s?.html) {
                    actioned.push(id);
                    populateSectionContent(s.key, sectionQuestions);
                }
            }

            const updatedPendingUpdates = pendingUpdates.filter((x) => !actioned.includes(x));

            if (pendingUpdates.length !== updatedPendingUpdates.length)
                setPendingUpdates(pendingUpdates.filter((x) => !actioned.includes(x)));
        }
    }, [sections, pendingUpdates, populateSectionContent, sectionQuestions]);

    const setValue = (type: string, key: string, value: any, id?: number | string) => {
        const keysToUpdate = [key];

        if (type === "placeholder") {
            setQuestions(questions.map((q) => (q.key === key ? { ...q, value } : q)));
            setPlaceholderValue(editor, key, value);
        }

        if (type === "section") {
            let updatedQuestions = sectionQuestions.map((q) =>
                q.id === id ? { ...q, value } : q,
            );

            if (value === "Yes") {
                const sameKeyQuestions = updatedQuestions.filter((x) => x.key === key && x.id !== id);
                for (const sameKeyQuestion of sameKeyQuestions) sameKeyQuestion.value = "No";
            }

            const currentSection: any = sections.find((x: any) => x.sectionId === id);
            if (!currentSection) return;

            const sameQuestionSections: any[] = sections.filter(
                (x: any) => x.question === currentSection.question && x.sectionId !== id,
            );
            const sectionIdsToChange = sameQuestionSections.map((x) => x.sectionId);
            const sameQuestionQuestions = updatedQuestions.filter((q) =>
                sectionIdsToChange.includes(q.id as any),
            );
            for (const sameQuestionQuestion of sameQuestionQuestions) {
                sameQuestionQuestion.value = value;
                keysToUpdate.push(sameQuestionQuestion.key);
            }

            updatedQuestions = updatedQuestions.map((q) =>
                sectionIdsToChange.includes(q.id as any) ? { ...q, value } : q,
            );

            setSectionQuestions(updatedQuestions);
            doUpdates(keysToUpdate, updatedQuestions);
        }
    };

    useEffect(() => {
        let changed = false;
        const tempQuestions = [...sectionQuestions];
        const sectionIds: (number | string)[] = [];

        for (const question of tempQuestions) {
            if (question.dependsOnSectionId) {
                const parentQuestion = tempQuestions.find((x) => x.id === question.dependsOnSectionId);
                if (parentQuestion) {
                    const hidden = parentQuestion.value !== "Yes";
                    if (question.hidden !== hidden) {
                        question.hidden = hidden;
                        changed = true;
                        const sectionId = populateSectionContent(question.key, tempQuestions);
                        if (sectionId) sectionIds.push(sectionId);
                    }
                }
            }
        }

        if (changed) setSectionQuestions(tempQuestions);
        if (sectionIds.length > 0) setPendingUpdates([...pendingUpdates, ...sectionIds]);

        for (const sectionId of sectionIds) dispatch(getSection(sectionId));
    }, [dispatch, pendingUpdates, populateSectionContent, sectionQuestions]);

    const saveAnswers = async () => {
        if (!selectedAccount?.externalId) return;
        const answers: Record<string, any> = {};

        let sectionKeys = sectionQuestions.map((x) => x.key);
        sectionKeys = [...new Set(sectionKeys)];

        for (const sectionKey of sectionKeys) {
            const sectionQuestionGroup = sectionQuestions.filter((x) => x.key === sectionKey);

            if (sectionQuestionGroup.length === 1) {
                answers[sectionKey] = sectionQuestionGroup[0].value;
            } else {
                const chosenQuestion = sectionQuestionGroup.find((x) => x.value === "Yes");
                answers[sectionKey] = chosenQuestion?.id;
            }
        }

        for (const question of questions) {
            if (!Object.prototype.hasOwnProperty.call(answers, question.key))
                answers[question.key] = question.value;
        }

        await documentService.saveAnswers(String(selectedAccount.externalId), answers);
    };

    const downloadPdf = async () => {
        setDownloading(true);
        saveAnswers();

        const converterOptions = {
            margin_right: pageMargins.right,
            margin_left: pageMargins.left,
            margin_top: pageMargins.top,
            margin_bottom: pageMargins.bottom,
            footer_html: '<div class="styled-counter"><span class="pageNumber"></span></div>',
            header_and_footer_css:
                ".styled-counter { font-size: 1em; color: hsl(0, 0%, 60%); text-align: center; margin-bottom:15px;}",
        };

        const response = await axios_raw.post(
            "https://ckeditor-pdf.greenwave-257981d3.westeurope.azurecontainerapps.io/v1/convert",
            {
                html: `<div class="ck-content">${editor.getData()}</div>`,
                css: ckcss,
                options: converterOptions,
            },
            { responseType: "blob" },
        );

        const blob = new Blob([response.data]);
        const link = window.document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("target", "_blank");
        link.download = `${template.name}.pdf`;
        link.click();

        setDownloading(false);
    };

    const downloadWord = async () => {
        setDownloading(true);
        saveAnswers();

        const converterOptions = {
            margin_right: pageMargins.right,
            margin_left: pageMargins.left,
            margin_top: pageMargins.top,
            margin_bottom: pageMargins.bottom,
            footer: [
                { html: "<span></span>", type: "first" },
                {
                    html: '<p><span class="pageNumber"></span></p>',
                    css: "p { font-size: 1em; color: hsl(0, 0%, 60%); text-align: center; margin-bottom:15px;}",
                },
            ],
        };

        try {
            const response = await axios_raw.post(
                "https://ckeditor-docx.greenwave-257981d3.westeurope.azurecontainerapps.io/v1/convert",
                {
                    html: `<html><link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Raleway:900"><body><div class="ck-content">${editor.getData()}</div></body></html>`,
                    css: ckcss,
                    options: converterOptions,
                },
                { responseType: "blob" },
            );

            const blob = new Blob([response.data]);
            const link = window.document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.setAttribute("target", "_blank");
            link.download = `${template.name}.docx`;
            link.click();
        } finally {
            setDownloading(false);
        }
    };

    const publish = () => setPublishing(true);

    const upload = async (confirmed: boolean) => {
        if (!selectedAccount?.accountId) return;

        if (!confirmed) {
            const docs = (await documentService.fetchManualAndHandbookFiles(
                selectedAccount.accountId,
            )) as string[];

            if (docs.includes(`${template.name}.pdf`)) {
                setRequireConfirmation(true);
                return;
            }
        }

        setRequireConfirmation(false);
        setDownloading(true);
        saveAnswers();

        const converterOptions = {
            margin_right: pageMargins.right,
            margin_left: pageMargins.left,
            margin_top: pageMargins.top,
            margin_bottom: pageMargins.bottom,
            footer_html: '<div class="styled-counter"><span class="pageNumber"></span></div>',
            header_and_footer_css:
                ".styled-counter { font-size: 1em; color: hsl(0, 0%, 60%); text-align: center; margin-bottom:15px;}",
        };

        const response = await axios_raw.post(
            "https://ckeditor-pdf.greenwave-257981d3.westeurope.azurecontainerapps.io/v1/convert",
            {
                html: `<body><div class="ck-content">${editor.getData()}</div></body>`,
                css: ckcss,
                options: converterOptions,
            },
            { responseType: "blob" },
        );

        const blob = new Blob([response.data]);

        const formData = new FormData();
        formData.append("file", blob);
        formData.append("fileName", `Manual and Handbook/${template.name}.pdf`);

        await documentService.uploadAccountFile(formData, selectedAccount.accountId, 1, 0);
        setPublishing(false);
        setSnack("File published", "success");
        setDownloading(false);
    };

    if (isLoading) return <h1>Loading ...</h1>;
    if (!template) return <h1>No template found ...</h1>;

    return (
        <Grid container spacing={3} style={styles.targetDiv}>
            <Grid size={3} style={{ height: "100%" }}>
                {selectedAccount && (
                    <Box display="flex" flexDirection="column" style={{ height: "100%" }}>
                        <Alert severity="info" onClose={() => setSelectedAccount(null)}>
                            {selectedAccount.accountName}
                        </Alert>
                        <Box mb={3}>
                            <Typography variant="h5">
                                Please complete the steps below to complete your document
                            </Typography>
                        </Box>
                        <Box mb={3}>
                            <LinearProgress variant="determinate" value={progress} />
                        </Box>
                        {stage === 1 && progress === 100 && (
                            <Box>
                                <Button
                                    variant="contained"
                                    onClick={() => setStage(2)}
                                    disabled={pendingUpdates.length > 0}
                                >
                                    Next
                                </Button>
                            </Box>
                        )}
                        {stage === 2 && (
                            <Box>
                                <Button variant="contained" onClick={() => setStage(1)}>
                                    Back
                                </Button>
                            </Box>
                        )}
                        <Box flexGrow={1} style={{ height: 0 }}>
                            <PerfectScrollbar>
                                <Paper style={{ padding: "8px" }}>
                                    {displayQuestions.map((q) => (
                                        <QuestionCard
                                            key={`${q.key}-${q.id}`}
                                            question={q}
                                            setValue={(value: any) => setValue(q.type, q.key, value, q.id)}
                                        />
                                    ))}
                                </Paper>
                            </PerfectScrollbar>
                        </Box>
                    </Box>
                )}
                {!selectedAccount && (
                    <Box display="flex" flexDirection="column">
                        <Typography variant="h6" gutterBottom>
                            Select an account
                        </Typography>
                        <Autocomplete<AccountOption, false, false, true>
                            freeSolo
                            open={autocompleteOpen}
                            onOpen={() => setAutocompleteOpen(true)}
                            onClose={() => setAutocompleteOpen(false)}
                            isOptionEqualToValue={(option, value) =>
                                typeof option !== "string" &&
                                typeof value !== "string" &&
                                option.value === value.value
                            }
                            getOptionLabel={(option) =>
                                typeof option === "string" ? option : option.text
                            }
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                    <li key={option.value} {...rest}>
                                        {option.text}
                                    </li>
                                );
                            }}
                            onChange={handleAccountChange}
                            options={accountOptions}
                            onInputChange={(_e, val) => setAccountText(val)}
                            renderInput={(params) => {
                                if (params.inputProps) {
                                    params.inputProps.autoComplete = "account-documents";
                                }
                                return <TextField {...params} fullWidth />;
                            }}
                        />
                    </Box>
                )}
            </Grid>
            <Grid size={9} style={{ height: "100%" }}>
                <Box display="flex" flexDirection="column" style={{ height: "100%" }}>
                    <Box mb={3} display="flex" justifyContent="space-between">
                        <Box display="flex">
                            <GavelIcon fontSize="small" />
                            &nbsp;
                            <Typography variant="h5">{template.name}</Typography>
                        </Box>
                    </Box>
                    <Box
                        mb={2}
                        flexGrow={1}
                        style={{ height: 0 }}
                        display="flex"
                        flexDirection="column"
                    >
                        <MyCKEditor
                            type="document"
                            setEditor={setEditor}
                            editor={editor}
                            onChange={() => {}}
                        />
                    </Box>
                    {progress === 100 && stage === 2 && (
                        <Box display="flex" justifyContent="flex-end" className="ew">
                            <Box>
                                <Button
                                    title="Publish to myWorkNest"
                                    disabled={downloading}
                                    onClick={publish}
                                >
                                    <PublishIcon className="ew grow" fontSize="large" />
                                </Button>
                            </Box>
                            <Button title="Download Word Document" disabled={downloading} onClick={downloadWord}>
                                <DocImage title="Download Word Document" className="ew grow docImage" />
                            </Button>
                            <Button title="Download PDF Document" disabled={downloading} onClick={downloadPdf}>
                                <PdfImage title="Download PDF Document" className="ew grow docImage" />
                            </Button>
                        </Box>
                    )}
                </Box>
            </Grid>
            <Dialog
                open={publishing}
                onClose={() => setPublishing(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>PUBLISH {template.name}</DialogTitle>
                <DialogActions>
                    {selectedAccount?.accountId && !requireConfirmation && (
                        <Button
                            disabled={downloading}
                            style={{ width: "100%", marginTop: "10px" }}
                            onClick={() => upload(false)}
                            variant="contained"
                            color="primary"
                        >
                            Publish to {selectedAccount.accountName}
                        </Button>
                    )}
                    {requireConfirmation && (
                        <Button
                            disabled={downloading}
                            style={{ width: "100%", marginTop: "10px" }}
                            onClick={() => upload(true)}
                            variant="contained"
                            color="primary"
                        >
                            Confirm Overwrite existing file
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default TemplateDocument;
